/**
 * Swatch King Price Integration
 * Updates product price when Swatch King variant changes
 */
(function() {
  'use strict';

  // Debounce and update tracking
  let updateTimeout = null;
  let isUpdating = false;
  let lastUpdatedVariantId = null;
  let lastUpdateTime = 0;

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Find all variant-king elements (Swatch King uses variant-king-combined-listing)
    const variantKingElements = document.querySelectorAll('variant-king-combined-listing, [class*="variant-king"], [id*="variant-king"]');
    
    if (variantKingElements.length === 0) {
      // Try to find Swatch King swatches container (the user mentioned .swatches class)
      const swatchContainers = document.querySelectorAll('.swatches, [class*="swatch"]');
      if (swatchContainers.length > 0) {
        setupSwatchKingListener(swatchContainers);
      }
    } else {
      variantKingElements.forEach(function(element) {
        setupSwatchKingListener([element]);
      });
    }

    // Always listen for changes to the variant input (Swatch King typically updates this)
    setupVariantInputListener();
    
    // Listen for Swatch King's custom events if they exist
    setupCustomEventListeners();
    
    // Also watch for clicks on swatch items directly
    setupDirectSwatchListeners();
  }

  function setupSwatchKingListener(elements) {
    elements.forEach(function(element) {
      // Listen for clicks on swatch options
      element.addEventListener('click', function(e) {
        // Small delay to let Swatch King update the variant input first
        setTimeout(function() {
          checkAndUpdatePrice();
        }, 10);
      }, true); // Use capture phase to catch events early

      // Listen for any changes within the element
      const observer = new MutationObserver(function(mutations) {
        // Check if any mutation might indicate a variant change
        let shouldUpdate = false;
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes') {
            const attrName = mutation.attributeName;
            if (attrName === 'class' || attrName === 'data-selected' || attrName === 'data-variant-id') {
              shouldUpdate = true;
            }
          } else if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            shouldUpdate = true;
          }
        });
        
        if (shouldUpdate) {
          // Small delay to let Swatch King update the variant input first
          setTimeout(function() {
            checkAndUpdatePrice();
          }, 10);
        }
      });

      observer.observe(element, {
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'data-variant-id', 'data-selected', 'style'],
        subtree: true
      });
    });
  }

  function setupDirectSwatchListeners() {
    // Listen for clicks on swatch items directly (Swatch King swatches)
    document.addEventListener('click', function(e) {
      const swatchItem = e.target.closest('.swatch, [class*="swatch"], [data-option], [data-variant-id]');
      if (swatchItem) {
        // Small delay to let Swatch King update the variant input first
        setTimeout(function() {
          checkAndUpdatePrice();
        }, 10);
      }
    }, true);
  }

  function setupVariantInputListener() {
    // Find the variant input that Swatch King updates
    const variantInputs = document.querySelectorAll('input[name="id"]');
    
    variantInputs.forEach(function(input) {
      let lastValue = input.value;
      
      // Use MutationObserver to watch for value changes
      const observer = new MutationObserver(function() {
        if (input.value !== lastValue) {
          lastValue = input.value;
          // IMMEDIATE update - no delay
          checkAndUpdatePrice();
        }
      });

      observer.observe(input, {
        attributes: true,
        attributeFilter: ['value']
      });

      // Also listen for input events
      input.addEventListener('change', function() {
        if (input.value !== lastValue) {
          lastValue = input.value;
          // IMMEDIATE update - no delay
          checkAndUpdatePrice();
        }
      });

      // Watch for value property changes (Swatch King might update this directly)
      const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (valueDescriptor) {
        Object.defineProperty(input, 'value', {
          get: valueDescriptor.get,
          set: function(newValue) {
            if (newValue !== lastValue) {
              lastValue = newValue;
              valueDescriptor.set.call(this, newValue);
              // IMMEDIATE update - no delay
              checkAndUpdatePrice();
            } else {
              valueDescriptor.set.call(this, newValue);
            }
          }
        });
      }
    });
  }

  function setupCustomEventListeners() {
    // Listen for common variant change event names that apps might use
    const eventNames = [
      'variant:change',
      'variantchange',
      'swatch:change',
      'swatchchange',
      'variant-king:change',
      'variantking:change'
    ];

    eventNames.forEach(function(eventName) {
      document.addEventListener(eventName, function(e) {
        if (e.detail && e.detail.variant) {
          // Check if this is a different variant to avoid unnecessary updates
          if (e.detail.variant.id !== lastUpdatedVariantId) {
            updatePriceFromVariant(e.detail.variant);
          }
        } else {
          // IMMEDIATE update - no delay
          checkAndUpdatePrice();
        }
      });
    });
  }

  // Debounce function - kept for compatibility but not used (all updates are now immediate)
  function debouncedCheckAndUpdatePrice(delay) {
    // IMMEDIATE update - no delay at all
    checkAndUpdatePrice();
  }

  function checkAndUpdatePrice(retryCount) {
    retryCount = retryCount || 0;
    const maxRetries = 5;
    
    // Get the current variant ID from the input
    const variantInput = document.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) {
      // Retry if input not ready yet (Swatch King might still be updating it)
      if (retryCount < maxRetries) {
        setTimeout(function() {
          checkAndUpdatePrice(retryCount + 1);
        }, 20);
      }
      return;
    }

    const variantId = parseInt(variantInput.value, 10);
    if (!variantId || isNaN(variantId)) {
      // Retry if variant ID not valid yet
      if (retryCount < maxRetries) {
        setTimeout(function() {
          checkAndUpdatePrice(retryCount + 1);
        }, 20);
      }
      return;
    }

    // Only skip if this is the exact same variant AND we just updated it (within 100ms)
    // This prevents duplicate updates but allows updates when variant actually changes
    const now = Date.now();
    if (variantId === lastUpdatedVariantId && (now - lastUpdateTime) < 100) {
      return;
    }

    // Prevent multiple simultaneous updates - but allow immediate retry if variant changed
    if (isUpdating && variantId !== lastUpdatedVariantId) {
      // Clear the updating flag and proceed immediately if variant changed
      isUpdating = false;
    } else if (isUpdating) {
      // If same variant and updating, skip
      return;
    }

    // Set updating flag
    isUpdating = true;
    lastUpdateTime = Date.now();

    // Get variant data from the variant-selects element
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) {
      isUpdating = false;
      // Retry if variant-selects not ready
      if (retryCount < maxRetries) {
        setTimeout(function() {
          checkAndUpdatePrice(retryCount + 1);
        }, 20);
      }
      return;
    }

    try {
      const variantData = variantSelects.getVariantData();
      if (!variantData || variantData.length === 0) {
        isUpdating = false;
        // Retry if variant data not ready
        if (retryCount < maxRetries) {
          setTimeout(function() {
            checkAndUpdatePrice(retryCount + 1);
          }, 20);
        }
        return;
      }
      
      const currentVariant = variantData.find(function(v) {
        return v.id === variantId;
      });

      if (currentVariant) {
        // Update the last updated variant ID
        lastUpdatedVariantId = variantId;
        
        updatePriceFromVariant(currentVariant);
        // Also trigger the theme's variant change event
        triggerThemeVariantChange(currentVariant, variantSelects);
        // Update variant input to ensure form is in sync
        updateVariantInput(currentVariant);
      } else {
        isUpdating = false;
        // Retry if variant not found in data yet
        if (retryCount < maxRetries) {
          setTimeout(function() {
            checkAndUpdatePrice(retryCount + 1);
          }, 20);
        }
      }
    } catch (e) {
      console.warn('Swatch King Price Integration: Could not get variant data', e);
      isUpdating = false;
      // Retry on error
      if (retryCount < maxRetries) {
        setTimeout(function() {
          checkAndUpdatePrice(retryCount + 1);
        }, 20);
      }
    }
  }

  function updatePriceFromVariant(variant) {
    if (!variant) {
      isUpdating = false;
      return;
    }
    
    // Update the last updated variant ID and time
    lastUpdatedVariantId = variant.id;
    lastUpdateTime = Date.now();

    // Get the section ID from variant-selects
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) {
      isUpdating = false;
      return;
    }

    const sectionId = variantSelects.dataset.section;
    if (!sectionId) {
      isUpdating = false;
      return;
    }

    // IMMEDIATE UPDATE: Update ALL prices synchronously with JavaScript formatting
    // NO DELAY - everything updates at once, right now
    updatePriceBlockImmediate('price-' + sectionId, variant);
    updatePriceBlockImmediate('price-' + sectionId + '--sticky', variant);
    updateCustomPriceBlocks(variant);
    updateAddToCartButton(variant, sectionId);
    
    // Background update from server (doesn't block, happens async)
    // This is only for any additional formatting, prices are already updated above
    updatePriceBlock('price-' + sectionId, variant);
    updatePriceBlock('price-' + sectionId + '--sticky', variant);
    updateCustomPriceBlocksFromServer(variant, sectionId);
    
    // Reset updating flag immediately (no delay)
    isUpdating = false;
  }
  
  function updateCustomPriceBlocksFromServer(variant, sectionId) {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;
    
    const productUrl = variantSelects.dataset.url;
    if (!productUrl) return;
    
    // Find all custom price blocks that might need server update
    const customPriceBlocks = document.querySelectorAll('[id^="lpv2-"], [id^="lpv2-basic-"]');
    if (customPriceBlocks.length === 0) return;
    
    // Fetch updated section from server
    fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`)
      .then(function(response) {
        return response.text();
      })
      .then(function(responseText) {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        customPriceBlocks.forEach(function(block) {
          const blockId = block.id;
          const source = html.getElementById(blockId);
          
          if (source && block) {
            // Update the price elements within the block
            const currentPriceEl = block.querySelector('[data-price-current], [data-lpv2-current]');
            const sourceCurrentPriceEl = source.querySelector('[data-price-current], [data-lpv2-current]');
            
            if (currentPriceEl && sourceCurrentPriceEl) {
              currentPriceEl.textContent = sourceCurrentPriceEl.textContent;
            }
            
            const comparePriceEl = block.querySelector('[data-price-compare], [data-lpv2-compare]');
            const sourceComparePriceEl = source.querySelector('[data-price-compare], [data-lpv2-compare]');
            
            if (comparePriceEl && sourceComparePriceEl) {
              comparePriceEl.textContent = sourceComparePriceEl.textContent;
              comparePriceEl.style.display = sourceComparePriceEl.style.display || '';
            }
          }
        });
      })
      .catch(function(e) {
        // If server update fails, fall back to JavaScript update
        console.warn('Swatch King: Could not update custom price blocks from server', e);
      });
  }

  // Get money format helper (shared function)
  function getMoneyFormat() {
    return (window.theme && window.theme.settings && window.theme.settings.money_with_currency_format) 
      ? window.theme.settings.money_with_currency_format 
      : (window.Shopify && window.Shopify.money_format) 
        ? window.Shopify.money_format 
        : '€{{amount}}';
  }

  // Format money helper (shared function)
  function formatMoney(cents, moneyFormat) {
    if (!moneyFormat) {
      moneyFormat = getMoneyFormat();
    }
    
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(cents, moneyFormat);
      } catch (e) {
        console.warn('Swatch King: Error formatting money', e);
      }
    }
    
    // Fallback: try to use global formatMoney from vendor if available
    if (typeof window.formatMoney === 'function') {
      try {
        return window.formatMoney(cents, moneyFormat);
      } catch (e) {
        // Continue to simple fallback
      }
    }
    
    // Simple fallback: format with euro sign and comma
    const amount = (cents / 100).toFixed(2);
    // Replace {{amount}} with the actual amount, handle both . and , as decimal separator
    let formattedAmount = amount;
    if (moneyFormat.includes(',')) {
      formattedAmount = amount.replace('.', ',');
    }
    return moneyFormat.replace(/\{\{amount\}\}/g, formattedAmount);
  }

  // Update price block immediately with JavaScript formatting (no server delay)
  function updatePriceBlockImmediate(priceBlockId, variant) {
    const priceBlock = document.getElementById(priceBlockId);
    if (!priceBlock || !variant) return;

    const moneyFormat = getMoneyFormat();
    const formattedPrice = formatMoney(variant.price, moneyFormat);
    const formattedComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price 
      ? formatMoney(variant.compare_at_price, moneyFormat) 
      : null;
    
    // Find price elements based on the theme's structure (from product-price.liquid)
    // Structure: <span class="price"><ins><span class="amount">{{ money_price }}</span></ins></span>
    const currentPriceEl = priceBlock.querySelector('ins .amount, ins span.amount, .price ins .amount, .price ins span.amount');
    const comparePriceEl = priceBlock.querySelector('del .amount, del span.amount, .price del .amount, .price del span.amount');
    
    // Also try other common selectors
    const allCurrentPriceSelectors = [
      'ins .amount',
      'ins span.amount',
      '.price ins .amount',
      '.price ins span.amount',
      '.price__regular .price-item--regular',
      '.price__sale .price-item--sale',
      '.price-item--regular',
      '.price-item--sale',
      '[data-price-current]',
      '[data-lpv2-current]',
      '.product-price .amount',
      'span.amount:not(del span.amount)'
    ];
    
    const allComparePriceSelectors = [
      'del .amount',
      'del span.amount',
      '.price del .amount',
      '.price del span.amount',
      '.price-item--compare',
      '.price__compare',
      '[data-price-compare]',
      '[data-lpv2-compare]'
    ];
    
    // Find current price element
    let currentEl = null;
    for (let i = 0; i < allCurrentPriceSelectors.length; i++) {
      currentEl = priceBlock.querySelector(allCurrentPriceSelectors[i]);
      if (currentEl) break;
    }
    
    // Find compare price element
    let compareEl = null;
    for (let i = 0; i < allComparePriceSelectors.length; i++) {
      compareEl = priceBlock.querySelector(allComparePriceSelectors[i]);
      if (compareEl) break;
    }
    
    // Update current price - IMMEDIATE
    if (currentEl) {
      currentEl.textContent = formattedPrice;
      currentEl.style.display = '';
    } else {
      // Fallback: try to find price by searching all text content
      // Look for any element containing a price pattern
      const allTextElements = priceBlock.querySelectorAll('span, div, p, ins, strong, b');
      let found = false;
      allTextElements.forEach(function(el) {
        if (found) return;
        const text = el.textContent.trim();
        // Check if this looks like a price (has euro sign or number pattern)
        if (text && (text.includes('€') || text.match(/[\d,.]{3,}/)) && !el.closest('del')) {
          // Check if it's not a compare price
          if (!el.classList.contains('price-item--compare') && !el.closest('.price__compare')) {
            // Update the text content
            el.textContent = formattedPrice;
            found = true;
          }
        }
      });
      
      // If still not found, update the entire price block's innerHTML
      if (!found && priceBlock.textContent) {
        // Replace any price-like pattern in the HTML
        const originalHTML = priceBlock.innerHTML;
        // Find and replace price patterns
        const pricePattern = /(€\s*)?[\d,.]+\s*(€)?/g;
        let replacementCount = 0;
        const updatedHTML = originalHTML.replace(pricePattern, function(match, p1, p2) {
          replacementCount++;
          // First match is usually the current price, rest might be compare prices
          if (replacementCount === 1) {
            return formattedPrice;
          } else if (replacementCount === 2 && formattedComparePrice) {
            return formattedComparePrice;
          }
          return formattedPrice;
        });
        if (replacementCount > 0) {
          priceBlock.innerHTML = updatedHTML;
        }
      }
    }
    
    // Update compare price - IMMEDIATE
    if (compareEl) {
      if (formattedComparePrice) {
        compareEl.textContent = formattedComparePrice;
        compareEl.closest('del') && (compareEl.closest('del').style.display = '');
        compareEl.style.display = '';
      } else {
        // Hide compare price
        const delElement = compareEl.closest('del');
        if (delElement) {
          delElement.style.display = 'none';
        }
        compareEl.style.display = 'none';
      }
    }
    
    // Also update any direct text content that looks like a price
    const textNodes = [];
    const walker = document.createTreeWalker(
      priceBlock,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node;
    while (node = walker.nextNode()) {
      const text = node.textContent.trim();
      if (text && (text.match(/€[\d,.]+\s*|[\d,.]+\s*€/) || text.match(/[\d,.]{3,}/))) {
        const parent = node.parentElement;
        // Only update if not already updated and not in a del tag (compare price)
        if (parent && !parent.closest('del') && parent.textContent !== formattedPrice && parent.textContent !== formattedComparePrice) {
          // Check if parent contains price-like content
          if (parent.textContent.match(/€|[\d,.]{3,}/)) {
            // Replace the price part
            parent.textContent = parent.textContent.replace(/(€\s*)?[\d,.]+\s*(€)?/g, formattedPrice);
          }
        }
      }
    }
    
    priceBlock.classList.remove('visibility-hidden');
  }

  function updatePriceBlock(priceBlockId, variant) {
    const priceBlock = document.getElementById(priceBlockId);
    if (!priceBlock) return;

    // Get the product URL for fetching updated price
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const productUrl = variantSelects.dataset.url;
    const sectionId = variantSelects.dataset.section;

    if (!productUrl || !sectionId) return;

    // Fetch updated price from server (same method as theme uses)
    // This runs in background after immediate update
    fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`)
      .then(function(response) {
        return response.text();
      })
      .then(function(responseText) {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.getElementById(priceBlockId);
        
        if (source && priceBlock) {
          priceBlock.innerHTML = source.innerHTML;
          priceBlock.classList.remove('visibility-hidden');
        }
        
        // Also update the add to cart button availability
        updateAddToCartButton(variant, sectionId);
      })
      .catch(function(e) {
        console.warn('Swatch King Price Integration: Could not update price block', e);
        isUpdating = false;
      });
  }
  
  function updateAddToCartButton(variant, sectionId) {
    if (!variant) return;
    
    const productForm = document.getElementById(`product-form-${sectionId}`);
    if (!productForm) return;
    
    // Find the product-form element - try multiple methods to ensure we get the right one
    let productFormElement = productForm.closest('.product-form');
    if (!productFormElement) {
      // Fallback: find by data-section attribute
      productFormElement = document.querySelector(`.product-form[data-section="${sectionId}"]`);
    }
    
    // Get custom add to cart text from data attribute
    let customAddToCartText = null;
    if (productFormElement) {
      customAddToCartText = productFormElement.getAttribute('data-custom-add-to-cart-text');
      if (!customAddToCartText) {
        // Try dataset property as fallback
        customAddToCartText = productFormElement.dataset.customAddToCartText;
      }
    }
    
    const submitButtons = document.querySelectorAll('.single-add-to-cart-button');
    if (!submitButtons || submitButtons.length === 0) return;
    
    submitButtons.forEach(function(submitButton) {
      const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
      
      if (!submitButtonText) return;
      
      if (!variant.available) {
        submitButton.setAttribute('disabled', 'disabled');
        submitButton.classList.add('sold-out');
        if (window.theme && window.theme.variantStrings) {
          submitButtonText.textContent = window.theme.variantStrings.soldOut || 'Sold out';
        } else {
          submitButtonText.textContent = 'Sold out';
        }
      } else {
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('sold-out');
        submitButton.classList.remove('loading');
        
        // Check if it's a pre-order template
        const template = productFormElement ? productFormElement.getAttribute('template') : '';
        
        if (template && template.includes('pre-order')) {
          if (window.theme && window.theme.variantStrings) {
            submitButtonText.textContent = window.theme.variantStrings.preOrder || 'Pre-order';
          } else {
            submitButtonText.textContent = 'Pre-order';
          }
        } else {
          // Use custom add to cart text if available, otherwise use default
          if (customAddToCartText && customAddToCartText.trim() !== '') {
            submitButtonText.textContent = customAddToCartText;
          } else {
            if (window.theme && window.theme.variantStrings) {
              submitButtonText.textContent = window.theme.variantStrings.addToCart || 'Add to cart';
            } else {
              submitButtonText.textContent = 'Add to cart';
            }
          }
        }
      }
    });
  }

  function updateCustomPriceBlocks(variant) {
    // Use shared formatMoney function
    const moneyFormat = getMoneyFormat();
    
    // Update lpv2_price blocks
    const lpv2PriceBlocks = document.querySelectorAll('[data-lpv2-current]');
    lpv2PriceBlocks.forEach(function(block) {
      const root = block.closest('[id^="lpv2-"]');
      if (!root) return;

      const currentEl = root.querySelector('[data-lpv2-current]');
      const compareEl = root.querySelector('[data-lpv2-compare]');
      const savingsEl = root.querySelector('[data-lpv2-savings]');
      const savedEl = root.querySelector('[data-lpv2-saved]');

      if (currentEl) {
        currentEl.textContent = formatMoney(variant.price, moneyFormat);

        if (compareEl && variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.style.display = 'inline';
          compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          if (savingsEl && savedEl) {
            const savings = variant.compare_at_price - variant.price;
            savedEl.textContent = formatMoney(savings, moneyFormat);
            savingsEl.style.display = 'block';
          }
        } else if (compareEl) {
          compareEl.style.display = 'none';
          if (savingsEl) savingsEl.style.display = 'none';
        }
      }
    });

    // Update lpv2_price_label blocks - use the same formatMoney function
    const lpv2LabelBlocks = document.querySelectorAll('[data-price-current]');
    lpv2LabelBlocks.forEach(function(block) {
      const root = block.closest('[id^="lpv2-basic-"]');
      if (!root) return;

      const currentEl = root.querySelector('[data-price-current]');
      const compareEl = root.querySelector('[data-price-compare]');
      const unavailableEl = root.querySelector('.price-unavailable');

      if (currentEl) {
        currentEl.textContent = formatMoney(variant.price, moneyFormat);

        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.style.display = 'inline';
            compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
          } else {
            compareEl.style.display = 'none';
          }
        }
        
        // Update availability message
        if (unavailableEl) {
          if (variant.available) {
            unavailableEl.style.display = 'none';
          } else {
            unavailableEl.style.display = 'block';
          }
        }
      }
    });

    // Trigger variant:change event for other listeners (this is important for lpv2_price_label)
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant: variant },
      bubbles: true
    }));
  }

  function updateVariantInput(variant) {
    if (!variant) return;
    
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;
    
    const sectionId = variantSelects.dataset.section;
    const productForms = document.querySelectorAll(`#product-form-${sectionId}, #product-form-installment`);
    
    productForms.forEach(function(productForm) {
      const input = productForm.querySelector('input[name="id"]');
      if (input && input.value !== variant.id.toString()) {
        input.value = variant.id;
        // Trigger change event to notify other listeners
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      }
    });
  }

  function triggerThemeVariantChange(variant, variantSelects) {
    // Dispatch the theme's variant change event
    if (typeof dispatchCustomEvent === 'function') {
      dispatchCustomEvent('product:variant-change', {
        variant: variant,
        sectionId: variantSelects.dataset.section
      });
    } else {
      // Fallback: dispatch custom event
      document.dispatchEvent(new CustomEvent('product:variant-change', {
        detail: {
          variant: variant,
          sectionId: variantSelects.dataset.section
        },
        bubbles: true
      }));
    }
  }

  // Also check on page load in case Swatch King has already set a variant
  setTimeout(function() {
    checkAndUpdatePrice();
  }, 500);
})();


